import React from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import ModalInfo from '../../../../theme/ui/Atoms/Modal/ModalInfo/ModalInfo';
import { useTranslation } from 'react-i18next';
import WorkspaceAPI from '../../../../redux/workspace/WorkspaceAPI';
import Button from '../../../../theme/ui/Atoms/Button/Button';
import useMessage from '../../../../shared/hooks/useMessage';
import useErrorMessage from '../../../../shared/hooks/useErrorMessage';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Input from '../../../../theme/ui/Atoms/Input/Input';
import WorkspaceInheritanceAPI from '../../../../redux/workspace/WorkspaceInheritance/WorkspaceInheritanceAPI';

interface RemoveWorkspaceModalProps {
  displayConfirmModal: boolean;
  setDisplayConfirmModal: (display: boolean) => void;
  workspaceId: string | null;
}
export const RemoveWorkspaceModal = (props: RemoveWorkspaceModalProps) => {
  const { displayConfirmModal, setDisplayConfirmModal, workspaceId } = props;
  const { data: workspace } = WorkspaceAPI.useGetWorkspaceByIdQuery(
    workspaceId || ''
  );
  const [removeWorkspace, { isLoading: isRemovingWorkspace }] =
    WorkspaceInheritanceAPI.useRemoveSubworkspaceMutation();
  const { t } = useTranslation();
  const showMessage = useMessage();
  const showErrorMessage = useErrorMessage();

  const validationSchema = yup.object({
    workspaceName: yup
      .string()
      .required(t('workspaces.name_required') ?? '')
      .test(
        'match',
        t('workspaces.workspace_name_must_match') ?? '',
        (value) => value === workspace?.name
      ),
  });

  const formik = useFormik({
    initialValues: {
      workspaceName: '',
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!workspaceId || !formik.isValid) return;
      try {
        await removeWorkspace({
          workspaceId: workspace?.parentId || '',
          subworkspaceId: workspaceId,
        })
          .unwrap()
          .then(() => {
            setDisplayConfirmModal(false);
            showMessage(
              t('workspaces.workspace_deleted_successfully', {
                workspace: workspace?.name,
              })
            );
          })
          .catch(() =>
            showErrorMessage(
              t('workspaces.workspace_error_deleted', {
                workspace: workspace?.name,
              })
            )
          );
      } catch (error) {
        showErrorMessage(
          t('workspaces.workspace_error_deleted', {
            workspace: workspace?.name,
          })
        );
      }
    },
  });

  const onDeleteClick = () => {
    formik.handleSubmit();
  };

  return (
    <ModalInfo
      open={displayConfirmModal}
      PaperProps={{
        sx: {
          padding: '2rem 4rem',
          width: '70vw',
        },
      }}
      description={
        <form onSubmit={formik.handleSubmit}>
          <Stack>
            <Typography
              variant="body1"
              component={'p'}
              p="2rem 0 0 0"
              align="center"
            >
              {t('workspaces.delete_workspace_confirmation', {
                workspace: workspace?.name,
              })}
            </Typography>
            <Typography
              variant="body1"
              component={'p'}
              p="0.5rem"
              align="center"
            >
              {t('workspaces.delete_workspace_confirmation1')}
            </Typography>
            <Typography
              variant="body1"
              component={'p'}
              p="0.5rem"
              align="center"
            >
              {t('workspaces.delete_workspace_confirmation2')}
            </Typography>

            <Input
              id="workspaceName"
              name="workspaceName"
              type="text"
              placeholder={workspace?.name ?? ''}
              required
              aria-required
              fullWidth
              value={formik.values.workspaceName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.workspaceName)}
              helperText={formik.errors.workspaceName || ''}
              sx={{ flex: 1 }}
              label={t('workspaces.name') ?? ''}
            />
          </Stack>
        </form>
      }
      title={
        <Typography fontSize="1.5rem" align="center">
          {t('workspaces.delete_workspace_name', {
            workspace: workspace?.name,
          })}
        </Typography>
      }
      buttonActions={
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-evenly"
          width={'100%'}
        >
          <Button
            autoFocus
            variant="outlined"
            rounded
            disabled={isRemovingWorkspace}
            onClick={() => {
              setDisplayConfirmModal(false);
            }}
            sx={{ flex: '1 1 0px' }}
          >
            {t('button.cancel')}
          </Button>

          <Button
            autoFocus
            variant="contained-gray"
            rounded
            disabled={isRemovingWorkspace || !formik.isValid}
            onClick={onDeleteClick}
            sx={{ flex: '1 1 0px' }}
          >
            {isRemovingWorkspace ? (
              <CircularProgress size="1rem" />
            ) : (
              t('button.delete')
            )}
          </Button>
        </Stack>
      }
    />
  );
};
